/**
 * LP共通トラッキング・DB送信ユーティリティ
 * 全LPで共通のID生成・DB送信ロジックを提供
 */

// === 定数定義 ===

// GAS WebアプリURL（全LP共通）
// 2026-07-16 リードDB統一: 旧LP共通DB(AKfycbzqxn…)→gas-questionnaire経由でAIシート「LP完結リード」へ集約
const GAS_ENDPOINT = "https://script.google.com/macros/s/AKfycbwMIUr0pXSiubpQ4w7mYoO8WE9RTanN2b1ZAcwPiqky0dh4K7oPvivWlo8asOBPuJPi/exec";

// localStorage/sessionStorageキー
const VISITOR_ID_KEY = "lp_common_visitor_id"; // 全LPで同じキーを使用（LP横断で同一ユーザーを識別）
const UTM_PARAMS_KEY = "lp_utm_params";
const AFF_PARAMS_KEY = "lp_aff_params";
const REFERRER_KEY = "lp_referrer";
const LANDING_PAGE_KEY = "lp_landing_page";

// === 型定義 ===

/**
 * フォームデータ型（個人情報10項目）
 */
export interface FormData {
  qualification?: string;    // 保有資格（カンマ区切り複数可）
  birth_year?: string;       // 生年（西暦4桁）
  birth_month?: string;      // 生月（1〜12）
  birth_day?: string;        // 生日（1〜31）
  name?: string;             // 氏名
  phone?: string;            // 電話番号（数字11桁）
  postal_code?: string;      // 郵便番号（数字7桁）
  prefecture?: string;       // お住まいの都道府県
  work_style?: string;       // 希望の働き方
  transfer_timing?: string;  // 転職希望時期
}

/**
 * DB送信用データ構造
 */
export interface LPSubmissionData {
  // ID系
  visitor_id: string;
  conversion_id: string;
  lp_id: string;

  // 個人情報10項目（取得しない項目は空文字）
  qualification: string;
  birth_year: string;
  birth_month: string;
  birth_day: string;
  name: string;
  phone: string;
  postal_code: string;
  prefecture: string;
  work_style: string;
  transfer_timing: string;

  // マーケティング情報
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  aff_company: string;
  aff_id: string;
  click_id: string;
  referrer: string;
  landing_page: string;

  // メタ情報
  submitted_at: string;
  user_agent: string;
}

// === UUID v4 生成 ===

/**
 * UUID v4 生成（RFC4122準拠）
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// === visitor_id 生成・管理 ===

/**
 * visitor_id を取得または生成
 * localStorage に保存し、再訪問時は既存のIDを返す
 * 形式：vis_{UUID v4}
 * 保存キー：lp_common_visitor_id（全LPで共通）
 *
 * @returns visitor_id（例：vis_a1b2c3d4-e5f6-7890-abcd-ef1234567890）
 */
export function getOrCreateVisitorId(): string {
  try {
    // localStorage から既存のIDを取得
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);

    if (!visitorId) {
      // 存在しない場合は新規生成
      visitorId = `vis_${generateUUID()}`;
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }

    return visitorId;
  } catch (error) {
    // localStorage が使えない場合はセッション限りのIDを返す
    console.error('localStorage error:', error);
    return `vis_${generateUUID()}`;
  }
}

// === conversion_id 生成 ===

/**
 * conversion_id を生成（フォーム送信ボタン押下時に毎回新規生成）
 * localStorageには保存しない（一回限りの使い捨て）
 * 形式：cvid_{YYYYMMDD}_{ランダム英数字8桁}
 *
 * @returns conversion_id（例：cvid_20250601_a1b2c3d4）
 */
export function generateConversionId(): string {
  // 現在日付を YYYYMMDD 形式で取得
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const dateStr = `${year}${month}${day}`;

  // ランダム英数字8桁を生成
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let randomStr = '';
  for (let i = 0; i < 8; i++) {
    randomStr += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return `cvid_${dateStr}_${randomStr}`;
}

// === LP_ID 自動取得 ===

/**
 * LP_ID をページのURLから自動取得
 * クエリパラメータを除いたURL（origin + pathname）を使用
 * 末尾スラッシュを統一（末尾スラッシュなしに統一）
 *
 * @returns lp_id（例："https://lp.nijikango.co.jp/nurse"）
 */
export function getLpId(): string {
  const origin = window.location.origin;
  // pathname の末尾スラッシュを除去（ルートパスの "/" は除く）
  const pathname = window.location.pathname.replace(/\/$/, '') || '/';
  return `${origin}${pathname}`;
}

// === URLパラメータ取得 ===

/**
 * URLパラメータを1つ取得
 */
function getURLParam(name: string): string {
  const params = new URLSearchParams(window.location.search);
  return params.get(name) || '';
}

/**
 * UTMパラメータを取得
 */
export function getUTMParams(): {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
} {
  return {
    utm_source: getURLParam('utm_source'),
    utm_medium: getURLParam('utm_medium'),
    utm_campaign: getURLParam('utm_campaign'),
    utm_term: getURLParam('utm_term'),
    utm_content: getURLParam('utm_content'),
  };
}

/**
 * アフィリエイトパラメータを取得
 */
export function getAffiliateParams(): {
  aff_company: string;
  aff_id: string;
  click_id: string;
} {
  return {
    aff_company: getURLParam('aff_company'),
    aff_id: getURLParam('aff_id'),
    click_id: getURLParam('click_id'),
  };
}

// === ページ表示時の初期化 ===

/**
 * ページ表示時（DOMContentLoaded）に呼び出す初期化処理
 * - visitor_id を生成・localStorage に保存
 * - UTM/アフィリエイトパラメータを sessionStorage に保存
 *   （ページ内でURLが変わった場合に備えて、フォーム送信時は sessionStorage から読み取る）
 */
export function initializeTracking(): void {
  try {
    // visitor_id を生成・保存（再訪問時は既存IDを返す）
    getOrCreateVisitorId();

    // UTMパラメータを sessionStorage に保存
    const utmParams = getUTMParams();
    sessionStorage.setItem(UTM_PARAMS_KEY, JSON.stringify(utmParams));

    // アフィリエイトパラメータを sessionStorage に保存
    const affParams = getAffiliateParams();
    sessionStorage.setItem(AFF_PARAMS_KEY, JSON.stringify(affParams));

    // リファラーを保存（初回のみ）
    if (!sessionStorage.getItem(REFERRER_KEY)) {
      sessionStorage.setItem(REFERRER_KEY, document.referrer);
    }

    // ランディングページURLを保存（初回のみ）
    if (!sessionStorage.getItem(LANDING_PAGE_KEY)) {
      sessionStorage.setItem(LANDING_PAGE_KEY, window.location.href);
    }
  } catch (error) {
    console.error('Tracking initialization error:', error);
  }
}

// === データ正規化 ===

/**
 * 電話番号からハイフンを除去して数字のみにする
 * 例："090-1234-5678" → "09012345678"
 */
function normalizePhone(value: string): string {
  return value.replace(/-/g, '');
}

/**
 * 郵便番号からハイフンを除去して数字のみにする
 * 例："150-0001" → "1500001"
 */
function normalizePostalCode(value: string): string {
  return value.replace(/-/g, '');
}

// === DB送信 ===

/**
 * フォームデータをGASデータベースに送信
 *
 * @param formData - フォーム入力データ（個人情報）
 * @param formFields - このLPで取得するフィールド名の配列（LP別に設定）
 *                     配列に含まれない項目は自動的に空文字("")で送信される
 * @returns 送信成功時 true、失敗時 false
 */
export async function submitToDatabase(
  formData: FormData,
  formFields: string[]
): Promise<boolean> {
  try {
    // visitor_id を localStorage から取得（ページ表示時に生成済み）
    const visitorId = getOrCreateVisitorId();

    // conversion_id をフォーム送信時に新規生成（使い捨て）
    const conversionId = generateConversionId();

    // lp_id をページのURLから自動取得
    const lpId = getLpId();

    // sessionStorage からマーケティング情報を読み取る
    // （ページ表示時に保存済み。URLが変わっても初回の値を保持）
    const utmParams = JSON.parse(sessionStorage.getItem(UTM_PARAMS_KEY) || '{}');
    const affParams = JSON.parse(sessionStorage.getItem(AFF_PARAMS_KEY) || '{}');
    const referrer = sessionStorage.getItem(REFERRER_KEY) || '';
    const landingPage = sessionStorage.getItem(LANDING_PAGE_KEY) || '';

    // 個人情報10項目を初期化（取得しない項目は空文字）
    const personalData: Record<string, string> = {
      qualification: '',
      birth_year: '',
      birth_month: '',
      birth_day: '',
      name: '',
      phone: '',
      postal_code: '',
      prefecture: '',
      work_style: '',
      transfer_timing: '',
    };

    // formFields に含まれる項目のみ formData から値を取得
    formFields.forEach((field) => {
      const value = formData[field as keyof FormData];
      if (value !== undefined && value !== null) {
        // 電話番号・郵便番号はハイフン除去して数字のみで送信
        if (field === 'phone') {
          personalData[field] = normalizePhone(value);
        } else if (field === 'postal_code') {
          personalData[field] = normalizePostalCode(value);
        } else {
          personalData[field] = value;
        }
      }
    });

    // DB送信用データ構造を構築
    const submissionData: LPSubmissionData = {
      // ID系（自動生成・自動取得）
      visitor_id: visitorId,
      conversion_id: conversionId,
      lp_id: lpId,

      // 個人情報10項目
      qualification: personalData.qualification,
      birth_year: personalData.birth_year,
      birth_month: personalData.birth_month,
      birth_day: personalData.birth_day,
      name: personalData.name,
      phone: personalData.phone,
      postal_code: personalData.postal_code,
      prefecture: personalData.prefecture,
      work_style: personalData.work_style,
      transfer_timing: personalData.transfer_timing,

      // マーケティング情報（sessionStorageから読み取り）
      utm_source: utmParams.utm_source || '',
      utm_medium: utmParams.utm_medium || '',
      utm_campaign: utmParams.utm_campaign || '',
      utm_term: utmParams.utm_term || '',
      utm_content: utmParams.utm_content || '',
      aff_company: affParams.aff_company || '',
      aff_id: affParams.aff_id || '',
      click_id: affParams.click_id || '',
      referrer,
      landing_page: landingPage,

      // メタ情報
      submitted_at: new Date().toISOString(),
      user_agent: navigator.userAgent,
    };

    // GASにPOST送信（no-corsモード：GASはCORSヘッダーを返さないため）
    await fetch(GAS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submissionData),
      mode: 'no-cors',
    });

    // no-corsモードでは response.ok が常に false になるため、
    // fetch が例外を投げなければ送信成功とみなす
    console.log('[lpTracking] Submitted to GAS:', submissionData);
    return true;
  } catch (error) {
    console.error('[lpTracking] Database submission error:', error);
    return false;
  }
}

/**
 * conversion_id を外部計測タグ（GTM DataLayer等）に渡す用途で生成
 * 個人情報を含まないため、外部タグに渡しても安全
 * ※ submitToDatabase() 実行後に呼び出すこと
 */
export function getConversionIdForTracking(): string {
  return generateConversionId();
}